// app/settings/pipeline-config/components/LeadSourcesConfig.tsx
import { useState, useEffect } from "react";

interface LeadSource {
  id: string;
  name: string;
  color: string;
  active: boolean;
  priority: "High" | "Medium" | "Low";
}

const defaultSources: LeadSource[] = [
  { id: "indiamart",  name: "IndiaMart",     color: "#f97316", active: true,  priority: "High"   },
  { id: "justdial",   name: "JustDial",      color: "#3b82f6", active: true,  priority: "Medium" },
  { id: "whatsapp",   name: "WhatsApp",      color: "#22c55e", active: true,  priority: "High"   },
  { id: "website",    name: "Website Forms", color: "#8b5cf6", active: true,  priority: "Medium" },
  { id: "social",     name: "Social Media",  color: "#ec4899", active: true,  priority: "Low"    },
  { id: "tradeindia", name: "TradeIndia",    color: "#ef4444", active: true,  priority: "Medium" },
  { id: "manual",     name: "Manual Entry",  color: "#64748b", active: true,  priority: "Low"    },
  { id: "referral",   name: "Referral",      color: "#14b8a6", active: false, priority: "High"   },
];

const priorityColors: Record<string, string> = {
  High:   "bg-green-100 text-green-700",
  Medium: "bg-amber-100 text-amber-700",
  Low:    "bg-gray-100 text-gray-600",
};

const colorOptions = ["#f97316","#3b82f6","#22c55e","#8b5cf6","#ec4899","#ef4444","#64748b","#14b8a6","#f59e0b"];

// ✅ Toggle — overflow-hidden prevents dot escaping
const Toggle = ({ checked, onChange, color = "bg-green-500" }: { checked: boolean; onChange: () => void; color?: string }) => (
  <button onClick={onChange}
    className={`relative w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 overflow-hidden ${checked ? color : "bg-gray-300"}`}>
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${checked ? "translate-x-5" : "translate-x-0"}`} />
  </button>
);

type Props = {
  sources: LeadSource[];
  onSourcesChange: (sources: LeadSource[]) => void;
  onSave: (sources: LeadSource[]) => Promise<void>;
};

export default function LeadSourcesConfig({ sources: propSources, onSourcesChange, onSave }: Props) {
  const [sources,        setSources]        = useState<LeadSource[]>(propSources.length > 0 ? propSources : defaultSources);
  const [editId,         setEditId]         = useState<string | null>(null);
  const [editName,       setEditName]       = useState("");
  const [newSourceName,  setNewSourceName]  = useState("");
  const [newSourceColor, setNewSourceColor] = useState("#64748b");
  const [isSaving,       setIsSaving]       = useState(false);

  useEffect(() => { if (propSources.length > 0) setSources(propSources); }, [propSources]);

  const update = (updated: LeadSource[]) => { setSources(updated); onSourcesChange(updated); };

  const handleToggle         = (id: string) => update(sources.map((s) => s.id === id ? { ...s, active: !s.active } : s));
  const handlePriorityChange = (id: string, priority: "High" | "Medium" | "Low") => update(sources.map((s) => s.id === id ? { ...s, priority } : s));
  const handleNameEdit       = (source: LeadSource) => { setEditId(source.id); setEditName(source.name); };
  const handleNameSave       = (id: string) => { if (editName.trim()) update(sources.map((s) => s.id === id ? { ...s, name: editName.trim() } : s)); setEditId(null); };
  const handleDelete         = (id: string) => update(sources.filter((s) => s.id !== id));

  const handleAddSource = () => {
    if (!newSourceName.trim()) return;
    const id = newSourceName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    if (sources.find((s) => s.id === id)) { return; }
    update([...sources, { id, name: newSourceName.trim(), color: newSourceColor, active: true, priority: "Low" }]);
    setNewSourceName(""); setNewSourceColor("#64748b");
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try { await onSave(sources); }
    catch { }
    finally { setIsSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h2 className="text-base font-semibold text-gray-900 mb-1">Lead Source Settings</h2>
      <p className="text-sm text-gray-500 mb-5">Configure which sources are active and their display names</p>

      {/* Table header */}
      <div className="grid grid-cols-12 gap-3 px-1 py-2 bg-gray-50 rounded-lg text-xs font-semibold uppercase text-gray-500 tracking-wider mb-1">
        <div className="col-span-5">Source</div>
        <div className="col-span-2 text-center">Active</div>
        <div className="col-span-3 text-center">Default Priority</div>
        <div className="col-span-2 text-center">Actions</div>
      </div>

      <div>
        {sources.map((source) => (
          <div key={source.id} className={`grid grid-cols-12 gap-3 px-1 py-3 border-b border-gray-100 items-center ${!source.active ? "opacity-50" : ""}`}>
            {/* Color + Name */}
            <div className="col-span-5 flex items-center gap-2.5 min-w-0">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: source.color }} />
              {editId === source.id ? (
                <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
                  onBlur={() => handleNameSave(source.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleNameSave(source.id); if (e.key === "Escape") setEditId(null); }}
                  autoFocus
                  className="flex-1 px-2 py-1 rounded border border-indigo-300 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              ) : (
                <button onClick={() => handleNameEdit(source)} className="text-sm font-medium text-gray-900 hover:text-indigo-600 transition-colors truncate">
                  {source.name}
                </button>
              )}
            </div>
            {/* Toggle ✅ */}
            <div className="col-span-2 flex justify-center">
              <Toggle checked={source.active} onChange={() => handleToggle(source.id)} color="bg-green-500" />
            </div>
            {/* Priority */}
            <div className="col-span-3 flex justify-center">
              <select value={source.priority} onChange={(e) => handlePriorityChange(source.id, e.target.value as "High" | "Medium" | "Low")}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer ${priorityColors[source.priority]}`}>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            {/* Actions */}
            <div className="col-span-2 flex justify-center gap-1">
              <button onClick={() => handleNameEdit(source)} className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-indigo-600 transition-colors">
                <i className="ri-pencil-line text-sm" />
              </button>
              <button onClick={() => handleDelete(source.id)} className="w-7 h-7 flex items-center justify-center rounded text-gray-300 hover:text-red-500 transition-colors">
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add new source */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <button className="w-6 h-6 rounded-full border-2 border-gray-200 flex-shrink-0 transition-transform hover:scale-110"
            style={{ backgroundColor: newSourceColor }}
            onClick={() => { const i = colorOptions.indexOf(newSourceColor); setNewSourceColor(colorOptions[(i + 1) % colorOptions.length]); }}
            title="Click to change color" />
          <input type="text" value={newSourceName} onChange={(e) => setNewSourceName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddSource(); }}
            placeholder="New source name..."
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all" />
        </div>
        <button onClick={handleAddSource} disabled={!newSourceName.trim()}
          className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50">
          + Add Source
        </button>
      </div>

      {/* Save button */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <button onClick={handleSaveAll} disabled={isSaving}
          className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2 whitespace-nowrap">
          {isSaving ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</> : "Save Lead Sources"}
        </button>
      </div>
    </div>
  );
}